import React, { Component } from 'react'
import { Provider } from './UploadsContext'
import noop from 'utils/noop'
import CurrentUser from 'components/CurrentUser'
import VideoUploadLinkQuery from 'components/VideoUploadLinkQuery'
import { CREATE_VIDEO_FROM_UUID } from 'mutations/CreateVideoFromUuid'
import { Mutation } from 'react-apollo'
import withFeature from 'components/withFeature'
import VIDEOS_QUERY from 'routes/videos.query'

const defaultUploadState = {
  progress: 0,
  state: 'uploading'
}

class UploadsProvider extends Component {
  static defaultProps = {
    onComplete: noop,
    onError: noop,
    onProgress: noop,
    onAbort: noop
  }

  state = {
    uploads: {}
  }

  updateUpload = (uuid, state) => {
    const { uploads } = this.state
    const updatedUpload = { ...uploads[uuid], ...state }
    this.setState({
      uploads: {
        ...uploads,
        [uuid]: updatedUpload
      }
    })
    return updatedUpload
  }

  removeUpload = ({ uuid }) => {
    const { uploads } = this.state
    const { [uuid]: _, ...rest } = uploads
    this.setState({
      uploads: rest
    })
  }

  abortUpload = ({ uuid }) => {
    this.state.uploads[uuid].req.abort()
  }

  startUpload = (file, url, uuid) => {
    const req = new XMLHttpRequest()
    const promise = new Promise((resolve, reject) => {
      req.open('PUT', url, true)

      req.addEventListener(
        'load',
        e => {
          const upload = this.updateUpload(uuid, {
            progress: 100,
            state: 'complete'
          })
          this.props.onComplete(upload)
          resolve(upload)
        },
        false
      )

      req.addEventListener(
        'error',
        e => {
          const upload = this.updateUpload(uuid, {
            hasError: true,
            state: 'error'
          })
          this.props.onError(e, upload)
          reject(e, upload)
        },
        false
      )

      req.upload.addEventListener(
        'progress',
        e => {
          const progress =
            e.total !== 0 ? parseInt((e.loaded / e.total) * 100, 10) : 0
          const upload = this.updateUpload(uuid, { progress })
          this.props.onProgress(upload)
        },
        false
      )

      req.addEventListener(
        'abort',
        e => {
          const upload = this.updateUpload(uuid, {
            progress: 0,
            state: 'cancelled'
          })
          this.props.onAbort(upload)
        },
        false
      )

      req.send(file)
    })

    this.setState({
      uploads: {
        ...this.state.uploads,
        [uuid]: { ...defaultUploadState, req, file, url, uuid }
      }
    })
    return promise
  }

  uploadsList = () => Object.values(this.state.uploads)

  componentWillUnmount() {
    this.uploadsList().forEach(({ req }) => req.abort())
    window.removeEventListener(this.beforeUnloadListener)
  }

  preventAbandonedUploads = e => {
    const activeUploadCount = this.uploadsList().filter(
      ({ state }) => state === 'uploading'
    ).length
    if (!activeUploadCount) {
      return
    }

    e.preventDefault()
  }

  componentWillMount() {
    this.beforeUnloadListener = window.addEventListener(
      'beforeunload',
      this.preventAbandonedUploads
    )
  }

  render() {
    if (!this.props.hasFeature('new_uploader')) {
      return this.props.children
    }

    const uploads = this.uploadsList().reverse()
    const emptyProvider = (
      <Provider value={{ uploads, ready: false }}>
        {this.props.children}
      </Provider>
    )
    return (
      <Mutation
        mutation={CREATE_VIDEO_FROM_UUID}
        update={(cache, { data: { createVideoFromUuid } }) => {
          const { viewer } = cache.readQuery({
            query: VIDEOS_QUERY
          })

          cache.writeQuery({
            query: VIDEOS_QUERY,
            data: {
              viewer: {
                ...viewer,
                videos: {
                  ...viewer.videos,
                  edges: [
                    {
                      node: createVideoFromUuid.video,
                      __typename: 'VideoEdge'
                    },
                    ...viewer.videos.edges
                  ]
                }
              }
            }
          })
        }}
      >
        {mutate => (
          <CurrentUser
            render={currentUser =>
              currentUser ? (
                <VideoUploadLinkQuery>
                  {({ loading, data, refetch }) =>
                    loading ? (
                      emptyProvider
                    ) : (
                      <Provider
                        value={{
                          uploads,
                          ready: true,
                          removeUpload: this.removeUpload,
                          abortUpload: this.abortUpload,
                          startUpload: file => {
                            refetch()
                            return this.startUpload(
                              file,
                              data.signedUploadUrl.url,
                              data.signedUploadUrl.uuid
                            ).then(() =>
                              mutate({
                                variables: {
                                  input: {
                                    uuid: data.signedUploadUrl.uuid,
                                    title: file.name
                                  }
                                }
                              }).then(() =>
                                this.removeUpload(data.signedUploadUrl)
                              )
                            )
                          }
                        }}
                      >
                        {this.props.children}
                      </Provider>
                    )
                  }
                </VideoUploadLinkQuery>
              ) : (
                emptyProvider
              )
            }
          />
        )}
      </Mutation>
    )
  }
}

export default withFeature(UploadsProvider)



// WEBPACK FOOTER //
// ./src/components/UploadsProvider.js